/**
 * LA shared add-to-cart — routes the custom cards (home "product showcase"
 * carousels, collection-grid quick-add, and build-a-pack) through Horizon's own
 * cart machinery so the cart drawer AND the free-shipping bar refresh live after
 * an add, without a page reload.
 *
 * Why this exists: the stock custom scripts only POSTed to /cart/add.js and then
 * called cart-items-component.fetchCartData() — but fetchCartData() is a data-only
 * fetch (it returns /cart.js JSON), it does NOT re-render the drawer DOM. So the
 * drawer opened showing the PRE-add cart until the shopper reloaded the page.
 *
 * Horizon refreshes the drawer by dispatching a `shopify:cart:lines-update` event
 * whose `promise` resolves with the new cart plus (optionally) pre-rendered section
 * HTML; `cart-items-component` morphs the drawer from `detail.sections`, or
 * refetches the section itself as a fallback when no HTML is supplied. The
 * free-shipping bar and cart-count badge listen to the same event. We mirror that
 * contract exactly here so every custom card behaves like a native add.
 *
 * Usage: window.LAAddToCart([{ id: <variantId>, quantity: <n> }, ...]) → Promise<cart>
 */
(function () {
  if (window.LAAddToCart) return;

  // Cart drawer/page sections to re-render server-side on add (so the morph is
  // instant instead of costing a second fetch). Mirrors product-form.js.
  function cartSectionIds() {
    var ids = [];
    document.querySelectorAll('cart-items-component[data-section-id]').forEach(function (el) {
      var id = el.dataset.sectionId;
      if (id && ids.indexOf(id) === -1) ids.push(id);
    });
    return ids;
  }

  function openDrawer() {
    // Covers the case where the merchant has auto-open-cart-drawer turned off.
    //
    // Must never TOGGLE. The header cart button is bound to `#cart-drawer/toggle`,
    // and <theme-drawer>.toggle() closes an already-open drawer — so clicking it
    // blind closed the drawer on every add made from inside it, i.e. every
    // "Goes well with" upsell add (snippets/la-cart-upsell.liquid). Bail when the
    // drawer is already open and call open() directly when the element has
    // upgraded; the trigger click is only the pre-upgrade fallback, and by then
    // the drawer is known to be closed so a toggle is equivalent to an open.
    var drawer = document.getElementById('cart-drawer');
    if (drawer) {
      if (drawer.hasAttribute('open')) return;
      if (typeof drawer.open === 'function') return drawer.open();
    }
    var trigger = document.querySelector('[aria-controls="cart-drawer"]');
    if (trigger) trigger.click();
  }

  function cartUrl() {
    return ((window.Theme && Theme.routes && Theme.routes.cart_url) || '/cart') + '.js';
  }

  window.LAAddToCart = function (items) {
    var addUrl = (window.Theme && Theme.routes && Theme.routes.cart_add_url) || '/cart/add.js';
    var sectionIds = cartSectionIds();

    var resolve, reject;
    var promise = new Promise(function (res, rej) {
      resolve = res;
      reject = rej;
    });

    // Fire the standard cart event up front so the drawer can auto-open and the
    // free-shipping bar can react; the listeners await this promise for the data.
    var evt = new CustomEvent('shopify:cart:lines-update', { bubbles: true });
    evt.action = 'add';
    evt.promise = promise;
    document.dispatchEvent(evt);

    var body = { items: items };
    if (sectionIds.length) body.sections = sectionIds.join(',');

    return fetch(addUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
      body: JSON.stringify(body),
      credentials: 'same-origin',
    })
      .then(function (r) {
        if (!r.ok) return r.json().then(function (e) { throw e; });
        return r.json();
      })
      .then(function (added) {
        // Authoritative cart for totals/counts (free-shipping bar, summaries).
        return fetch(cartUrl(), { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            // cart-icon.js reads `cart.totalQuantity` (the Storefront-API camelCase
            // shape) to render the header bubble; the raw /cart.js AJAX cart only has
            // `item_count`. Add it so the bubble updates. Snake_case fields stay intact
            // for the free-shipping bar (reads total_price / item_count).
            cart.totalQuantity = cart.item_count;
            resolve({
              cart: cart,
              detail: {
                items: cart.items,
                itemCount: cart.item_count,
                sections: added.sections,
                didError: false,
                source: 'la-cart-add',
              },
            });
            openDrawer();
            return cart;
          });
      })
      .catch(function (err) {
        reject(err);
        throw err;
      });
  };
})();
